import { Injectable } from '@angular/core';
import { CacheType, NetworkService } from '../network/network.service';
import { AlertController, Platform } from '@ionic/angular';
import { TextToSpeech } from '@capacitor-community/text-to-speech';
import { ProfileService } from '../profile/profile.service';
import { VoicePreference, VoiceServiceSettings } from './voice.model';
import { LanguageService } from 'src/app/language/language.service';
import { AppLauncher } from '@capacitor/app-launcher';
import { SoundService } from '../sound/sound.service';
import { UserStorageService } from '../storage/user-storage.service';
import { UserService } from '../user/user.service';
import { defaultCustomVoices } from '../manual-settings/manual-settings.data';
import { MinimaxSpeechEmotions } from './voice.model';

@Injectable({
  providedIn: 'root'
})
export class VoiceService {

  lang = 'pt-BR';

  settings: VoiceServiceSettings;

  languageSupportedVoices: VoicePreference[] = [];

  private supportedVoices: VoicePreference[] = [];

  private defaultCapacitorTtsIndex = 0;

  private activeVoice: VoicePreference;

  private languageSupported = false;
  private state: {
    primaryAvailable: boolean;
    secondaryAvailable: boolean;
  };

  currentText: string;

  private utterancePending = false;
  private apiFailTimestamp = 0;
  private apiFailCount = 0;

  constructor(
    private network: NetworkService,
    public platform: Platform,
    private profile: ProfileService,
    private langService: LanguageService,
    private alertController: AlertController,
    public sound: SoundService,
    private user: UserService
  ) {

    // Watch for lang changes
    this.langService.langObs.subscribe((lang) => {
      this.lang = lang;
      this.loadVoices(this.lang);
    });

    this.profile.voiceSettings$.subscribe((settings) => {
      this.settings = settings;
    });

    this.state = {
      primaryAvailable: true,
      secondaryAvailable: true
    };
    this.loadVoices(this.lang);
  }

  async speak(sentence: string, application: string, settings?: VoicePreference): Promise<any> {
    if (settings) {
      this.activeVoice = settings;
    } else if (this.state.primaryAvailable && this.settings.primaryVoice) {
      this.activeVoice = this.settings.primaryVoice;
    }
    else if (this.settings.secondaryVoice) {
      this.activeVoice = this.settings.secondaryVoice;
    }
    else {
      this.activeVoice = undefined;
    }
    console.log('[voice] activeVoice:', this.activeVoice);

    switch (this.activeVoice?.engine) {
      case 'default':
      case 'system':
        return this.speakDefault(sentence, this.activeVoice);
      case 'eleven':
      case 'minimax':
        //const teste =  await this.getBlobToStream(sentence, this.activeVoice.voiceURI)
        // this.storage.saveMedia('.txt', 'teste').then(async (file: string) => {
        //   console.log('[storage] result file', file);
        //   console.log('[storage] result read', await this.storage.getMedia(file));
        // });
        // this.storage.removeMedia('ffb54615-a4c1-4d3e-ab48-2197945158e1.txt');
        return this.speakCloud(sentence, this.activeVoice, application);
      default:
        return this.speakDefault(sentence, this.activeVoice);
    }
  }

  speakSystem(text: string) {
    return this.speakDefault(text);
  }

  speakCloud(sentence: string, voice: VoicePreference, application: string): Promise<{ audioBlob: Blob }> {
    return new Promise((resolve, reject) => {
      this.utterancePending = true;
      if (this.apiFailCount > 10 || Date.now() - this.apiFailTimestamp < 10000) {
        this.apiFailCount = 0;
        this.speakFallback(sentence, resolve, reject);
      }
      else {
        // setTimeout(() => { // Timer de Fallback
        //   this.speakFallback(sentence, resolve, reject);
        // }, 20000 + sentence.length * 50); // Timeout increases with sentence length
        this.network.getTtsStreamV2(
          sentence.replace('conversia', 'Convérsia'), 
          voice.engine, 
          CacheType.default, 
          voice.voiceURI || voice['voiceUri'], 
          voice.emotion, 
          application, 
          voice.pitch, 
          voice.rate, 
          voice.volume)
          .then(async (res) => {
            //this.utterancePending = false;
            const reader = res.getReader();
            const mediaSource = new MediaSource();
            const fullAudio: Uint8Array[] = [];

            mediaSource.addEventListener('sourceopen', async () => {
              //console.log('[elevenTts]', 'sourceopen');
              let done = false;
              let streamHead: ReadableStreamReadResult<Uint8Array>;
              const sourceBuffer = mediaSource.addSourceBuffer('audio/mpeg');

              sourceBuffer.addEventListener('updateend', () => {
                //console.log('[elevenTts] end Stream', 'updateend');
                if (done) {
                  //console.log('[elevenTts] end Stream', 'done');
                  mediaSource.endOfStream();
                  //this.onElevenSpeakEnd();

                  // // Salvar o audioBlob
                  // const audioBlob = new Blob(fullAudio, { type: 'audio/mpeg' });
                  // resolve({ audioBlob });
                }
              });

              while (!done) {
                let buffer: Uint8Array[] = [];
                let readerPromise: Promise<ReadableStreamReadResult<Uint8Array>>;
                while (true) {
                  // Verificar se já um pacote disponível
                  let data
                  if (streamHead) {
                    data = streamHead;
                    streamHead = undefined
                  } else {
                    readerPromise = reader.read();
                    try {
                      data = await Promise.race<ReadableStreamReadResult<Uint8Array>>([readerPromise, new Promise(resolve => setTimeout(resolve, 100))]);
                    } catch (error) {
                      console.error('[elevenTts] Error reading stream:', error);
                      reject(error);
                      return;
                    }
                  }

                  // Não há pacote disponível
                  if (data === undefined) {
                    //console.log('[elevenTts] end Stream', 'timeout');
                    if (buffer.length > 0) {
                      //console.log('[elevenTts] end Stream', 'appendBuffer');
                      // convert buffer to a single Uint8Array and add to the audio buffer
                      sourceBuffer.appendBuffer(this.uint8ArrayArrayToUint8Array(buffer));
                      fullAudio.push(this.uint8ArrayArrayToUint8Array(buffer));
                    }
                    // clear current buffer
                    try {
                      streamHead = await readerPromise;
                    } catch (error) {
                      console.error('Error reading stream:', error);
                      reject(error);
                      return;
                    }
                    break;
                  }
                  // Stream encerrada
                  else if (data?.done) {
                    //console.log('[elevenTts] end Stream', 'reader done');
                    if (buffer.length > 0) {
                      //console.log('[elevenTts] end Stream', 'appendBuffer');
                      // convert buffer to a single Uint8Array and add to the audio buffer
                      sourceBuffer.appendBuffer(this.uint8ArrayArrayToUint8Array(buffer));
                      fullAudio.push(this.uint8ArrayArrayToUint8Array(buffer));
                    }

                    done = true;
                    //console.log('[elevenTts] end Stream', 'done');
                    //mediaSource.endOfStream();
                    this.onElevenSpeakEnd();

                    // Salvar o audioBlob
                    const audioBlob = new Blob(fullAudio, { type: 'audio/mpeg' });
                    resolve({ audioBlob });
                    break;
                  }
                  // Acumular dados já recebidos
                  else {
                    //console.log('[elevenTts]', 'push', data);
                    buffer.push(data.value);
                  }
                }
              }
            });


            //console.log('[elevenTts]', 'playStream');
            this.sound.requestSound(URL.createObjectURL(mediaSource), 2, 1)
              .then((res) => {
                //console.log('[elevenTts]', 'playStream res', res);
                this.utterancePending = false;
              }, (error) => {
                console.warn('[elevenTts]', 'playStream error', error);
                reject(error);
              });

          }, (err) => {
            console.warn('[elevenTts]', 'getTtsStream error', err);
            this.speakFallback(sentence, resolve, reject);
            //reject(err);
          });
          console.log('[voice] speakCloud end');
      }
    });
  }

  // async speakMinimax(sentence: string, voice: VoicePreference, application: string): Promise<{ audioBlob: Blob }> {
  //   console.log('[voice] speakMinimax', sentence, voice, application);

  //   const generateVoice = await this.network.getTts(
  //     sentence.replace('conversia', 'Convérsia'), 
  //     'minimax', 
  //     voice.voiceURI || voice['voiceUri'], 
  //     CacheType.default, 
  //     MinimaxSpeechEmotions.AUTO, 
  //     voice.pitch, 
  //     voice.rate, 
  //     voice.volume, 
  //     voice.languageBoost
  //   );

  //   console.log('[minimaxTts] generateVoice', generateVoice);
  //   const audioBlob = new Blob([generateVoice.audio], { type: 'audio/mpeg' });
  //   const blobUrl = URL.createObjectURL(audioBlob);
  //   console.log('[minimaxTts] blobUrl', blobUrl);
  //   const soundObj = await this.sound.requestSound(blobUrl, 1, 1);
  //   URL.revokeObjectURL(blobUrl);

  //   return { audioBlob: audioBlob };
  // }

  uint8ArrayArrayToUint8Array(buffer: Uint8Array[]): Uint8Array {
    let offset = 0;
    const bufferLength = buffer.reduce((acc, val) => acc + val.length, 0);
    const bufferArray = new Uint8Array(bufferLength);
    buffer.forEach((chunk) => {
      bufferArray.set(chunk, offset);
      offset += chunk.length;
    });
    return bufferArray;
  }

  /**
   * Get a Blob with the audio data of the TTS
   * Audio is not played, only returned as a Blob
   * @param sentence Sentence to be spoken
   * @param voice Voice to be used
   */
  getBlobTtsBlob(sentence: string, voice?: VoicePreference): Promise<{ audioBlob: Blob }> {
    let useThisVoice: VoicePreference;
    if (voice) {
      useThisVoice = voice;
    } else if (this.state.primaryAvailable && this.settings.primaryVoice) {
      useThisVoice = this.settings.primaryVoice;
    }

    // Return undefined if using local voice
    if (useThisVoice.engine === 'system' || useThisVoice.engine === 'default') {
      return new Promise((resolve, reject) => {
        resolve(undefined);
      });
    }
    return new Promise((resolve, reject) => {
      console.log('[voice] getBlobTtsBlob', sentence, useThisVoice);
      this.network.getTtsV2(sentence.replace('conversia', 'Convérsia'), 'eleven', useThisVoice.voiceURI || useThisVoice['voiceUri'], CacheType.default)
        .then(async (res) => {
          res.subscribe({
            next: (data) => {
              console.log('getBlobToStream');
              resolve({ audioBlob: data.body });
            },
            error: (err) => {
              console.log('error subscribe of getTtsStreamV1', err);
              reject();
            }
          });
        }, (err) => {
          console.log('error promise of getTtsStreamV1', err);
          reject();
        });
    });
  }

  speakFallback(sentence: string, resolver: any, rejecter: any) {
    if (this.utterancePending) {
      console.log('speakFallback');
      this.apiFailCount++;
      this.apiFailTimestamp = Date.now();
      this.utterancePending = false;
      this.speakDefault(sentence).then(() => {
        this.utterancePending = false;
        resolver();
      }, () => {
        rejecter();
      });
    }
  }

  onElevenSpeakEnd() {
    this.apiFailCount = 0;
    console.log('onElevenSpeakEnd');
  }

  async speakDefault(text: string, voice?: VoicePreference): Promise<void> {
    console.log('[voice] speakDefault', text, voice);
    if (!this.languageSupported) {
      await this.loadVoices(this.lang);
    }
    if (!this.languageSupported) {
      this.unsupportedLanguageAlert();
      throw new Error('Unsupported language');
    }
    // If the text is empty, there is nothing to speak
    else if (text === undefined || text === '') {
      return;
    } else {
      const index = voice ? this.getVoiceIndex(voice) : undefined;
      console.log('speakDefault', index, voice, this.supportedVoices[index]);
      return TextToSpeech.speak({
        text,
        lang: voice?.lang || 'pt-BR',
        rate: voice?.rate || 1.0,
        pitch: voice?.pitch || 1.0,
        volume: voice?.volume || 1.0,
        category: 'playback',
        voice: index
      });
    }

  }

  pauseVoice() {
    if (!this.activeVoice) {
      console.warn('[voice] pauseVoice: No active voice');
      return;
    }
    console.log('[voice] pauseVoice');
    TextToSpeech.stop();
    switch (this.activeVoice.engine) {
      case 'default':
        TextToSpeech.stop();
        break;
      case 'eleven':
        this.sound.pauseRequest(2, this.activeVoice.voiceURI)
        //this.audio.pause();
        break;

      default:
        break;
    }
  }

  playVoice(voice: VoicePreference) {
    console.log('[voice] playVoice', voice);
    TextToSpeech.speak({
      text: this.currentText,
      lang: voice?.lang || 'pt-BR',
      rate: voice?.rate || 1.0,
      pitch: voice?.pitch || 1.0,
      volume: voice?.volume || 1.0,

      category: 'playback',
      voice: this.getVoiceIndex(voice)
    });
  }

  // Carregas a lista de vozes disponíveis
  async loadVoices(lang: string) {
    try {
      const isLanguageSupportedResult = await TextToSpeech.isLanguageSupported({ lang: lang });
      const isLanguageSupportedResultMobileAndroid = await TextToSpeech.isLanguageSupported({ lang: lang.replace('-', '_') });
      //console.log('[voice] isLanguageSupported:', lang, JSON.stringify(isLanguageSupportedResult));
      this.languageSupported = isLanguageSupportedResult.supported || isLanguageSupportedResultMobileAndroid.supported;

      this.supportedVoices = (await TextToSpeech.getSupportedVoices()).voices.map((voice) => {
        return {
          voiceURI: voice.voiceURI,
          name: voice.name,
          lang: voice.lang,
          localService: voice.localService,
          default: voice.default,
          engine: 'system'
        };
      });
      // console.log('[voice] getSupportedVoices:', JSON.stringify(getSupportedVoicesResult));
      // getSupportedVoicesResult.voices.forEach((voice) => {
      //   console.log('[voice] voice:', JSON.stringify(voice));
      // });

      // Filter voices by language
      this.languageSupportedVoices = this.supportedVoices.filter((voice) => this.langFilterPtBr(voice));
      //console.log('[voice] voiceList:', this.languageSupportedVoices);

      let preferredVoice = this.languageSupportedVoices.findIndex((voice) => voice.lang === lang && voice.name.includes('Google'));
      if (preferredVoice < 0) {
        preferredVoice = this.languageSupportedVoices.findIndex((voice) => voice.lang === lang);
      }
      if (preferredVoice < 0) {
        preferredVoice = this.languageSupportedVoices.findIndex((voice) => voice.voiceURI === 'pt-br-x-afs-local');
      }
      if (preferredVoice < 0) {
        preferredVoice = this.languageSupportedVoices.findIndex((voice) => voice.lang.startsWith('por-x'));
      }
      if (preferredVoice < 0) {
        preferredVoice = undefined;
      }
      this.defaultCapacitorTtsIndex = preferredVoice;
      //console.log('[voice] preferredVoice:', preferredVoice);
    } catch (error) {
      console.error('[voice] Error loading voices:', error);
    }
  }

  getVoiceIndex(voice: VoicePreference) {
    const index = this.supportedVoices.findIndex((v) => v.voiceURI === voice.voiceURI);
    return index >= 0 ? index : undefined;
  }

  langFilterPtBr(voice: VoicePreference) {
    return voice.lang === 'pt-BR' || voice.lang.startsWith('por-x');
  }
  cancelSpeak() {
    TextToSpeech.stop();

    // switch (this.activeVoice.engine) {
    //   case 'default':
    //     TextToSpeech.stop();
    //     break;
    //   case 'eleven':
    //     //this.audio.pause();
    //     break;

    //   default:
    //     break;
    // }
  }

  saveSettings() {
    this.profile.voiceSettings = this.settings;
    //this.profile.commit();
  }

  voicePreferenceCompareWith(o1: VoicePreference, o2: VoicePreference) {
    return o1 && o2 ? (o1.engine === o2.engine && o1.voiceURI === o2.voiceURI) : o1 === o2;
  }

  // async getCustomVoices() {
  //   return this.settings.customVoices;
  // }

  getUserCustomVoices() {
    const allVoices = defaultCustomVoices.concat(this.user.customVoices || []);
    // Filter out duplicates by keeping only the first occurrence of each voiceURI
    return allVoices.filter((voice, index, self) =>
      index === self.findIndex((v) => v.voiceURI === voice.voiceURI)
    );
  }

  async unsupportedLanguageAlert() {
    if (this.platform.is('capacitor')) {
      const alert = await this.alertController.create({
        header: 'Voz não encontrada',
        //subHeader: 'A Sub Header Is Optional',
        message: 'Instale o pacote de voz em Português nas configurações do Android > Gerenc. Geral > Leitura de Texto',
        buttons: ['OK'],
      });

      await alert.present();

      const { role } = await alert.onDidDismiss();

      // Open TTS settings
      await AppLauncher.openUrl({ url: 'com.android.settings:TTS_SETTINGS' }); // TODO: Não funciona
    } else {
      console.log('Error: Not running on Capacitor platform');
    }
  }
}
